<script setup>
import { ref, computed } from 'vue'
import { formatRiskDollars } from '../helpers'
import TextWithDefinitions from '../../TextWithDefinitions.vue'
import { convertDate, isToday } from '../../../utils/index'

const props = defineProps({
  item: { type: Object, default: null },
  mode: { type: String, default: 'completed' },
  employerName: { type: String, default: '' },
  riskScoreBefore: { type: String, default: '--' },
  riskAmountBefore: { type: String, default: '--' },
  currentRiskScore: { type: String, default: '--' },
  currentRiskAmount: { type: String, default: '--' },
  projectedRiskScore: { type: String, default: '--' },
  projectedRiskAmount: { type: String, default: '--' },
  isCalculatingProjection: { type: Boolean, default: false },
  planStartDate: { type: String, default: null }
})

const emit = defineEmits(['back', 'go-to-mark-complete', 'close-panel'])

const assessmentQuestionExpanded = ref(false)
const definitionExpanded = ref(false)
const notesExpanded = ref(false)

const isCompleted = computed(() => props.mode === 'completed')

function getTitle(item) {
  if (!item) return ''

  if (isCompleted.value) {
    let title = item.question_action_center_title || item.question_title || ''
    if (typeof title === 'string') {
      try {
        const parsed = JSON.parse(title)
        if (Array.isArray(parsed)) title = parsed[0] || ''
        else title = parsed
      } catch (e) { /* Keep as-is */ }
    }
    if (Array.isArray(title)) title = title[0] || ''
    return title || `Question ${item.question_id}`
  }

  return item.actionCenterTitle || item.title || item.body || `Question ${item.questionId}`
}

function getQuestionText() {
  return props.item?.question_text || null
}

function getDefinitionText() {
  const raw = props.item?.body || props.item?.question_action_item || props.item?.action_item || null
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed.filter(Boolean).join('\n')
      return String(parsed)
    } catch { return raw }
  }
  if (Array.isArray(raw)) return raw.filter(Boolean).join('\n')
  return String(raw)
}

function formatDateLong(date, excludeYear = false) {
  if (!date) return ''
  let options = { year: 'numeric', month: 'long', day: 'numeric' }
  if (excludeYear) {
    delete options.year
  }
  return date.toLocaleDateString('en-US', options)
}

function executeStoredFunction(storedFunction) {
  if (!storedFunction) return []
  try {
    const getActionItemDueDate = new Function('planStartDate', 'formatDate', storedFunction)
    return getActionItemDueDate(props.planStartDate, formatDateLong) || []
  } catch {
    return []
  }
}

function getDueDates() {
  const formula = props.item?.action_item_due_date
  if (!formula) return []
  const result = executeStoredFunction(formula)
  return Array.isArray(result) ? result : []
}

function formatSource(source) {
  if (source === 'action_center') return 'Action Center'
  if (source === 'assessment_tool') return 'Assessment'
  return source || ''
}

function formatShortDate(isoString) {
  if (!isoString) return ''
  const date = new Date(isoString)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = String(date.getFullYear()).slice(2)
  return `${month}/${day}/${year}`
}

function getNoteDate() {
  const dateStr = props.item?.note_updated_at
  if (!dateStr) return null
  if (isToday(dateStr)) {
    return 'Today'
  }
  return convertDate(dateStr)
}

function getUserDisplay() {
  const name = props.item?.user_fullname || props.item?.note_updated_by_name
  const role = props.item?.user_role
  if (!name) return null
  return role ? `${name}, ${role}` : name
}

function getImprovedRiskScore() {
  const after = props.item?.compliance_snapshot_after
  const before = props.item?.compliance_snapshot_before
  const afterPct = after?.percentage_compliance_risk
  const beforePct = before?.percentage_compliance_risk

  if (afterPct == null) return null

  const afterLabel = `${Math.round(afterPct)}%`
  const beforeLabel = beforePct != null ? `${Math.round(beforePct)}%` : props.riskScoreBefore
  return { afterLabel, beforeLabel }
}

function getDecreasedRiskAmount() {
  const after = props.item?.compliance_snapshot_after
  const before = props.item?.compliance_snapshot_before
  const afterAmt = after?.total_compliance_risk
  const beforeAmt = before?.total_compliance_risk

  if (afterAmt == null) return null

  const afterLabel = formatRiskDollars(afterAmt)
  const beforeLabel = beforeAmt != null ? formatRiskDollars(beforeAmt) : props.riskAmountBefore
  return { afterLabel, beforeLabel }
}
</script>

<template>
  <div class="detail-view">
    <!-- Back button -->
    <div class="mb-3">
      <button
        class="btn btn-link p-0 text-decoration-none text-primary text-14"
        type="button"
        @click="emit('back')"
      >
        <strong class="text-start">
          <i class="bi bi-chevron-left"></i>
          {{ isCompleted ? 'Back to Mark Requirements Complete' : `Back to All ${employerName} Action Items` }}
        </strong>
      </button>
    </div>

    <!-- Title -->
    <p class="header-20 mb-3 mt-4"><strong>{{ getTitle(item) }}</strong></p>

    <!-- Due date -->
    <template v-if="getDueDates().length > 0">
      <div class="d-flex text-16 mb-3.5 mt-2">
        <div class="me-1">Due:</div>
        <div class="d-flex flex-column">
          <div v-for="(dueDate, idx) in getDueDates()" :key="idx">
            <strong>
              <TextWithDefinitions :text="dueDate.date || ''" />
            </strong>
            <p v-if="dueDate.description">{{ dueDate.description }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Completed mode: badge + reduced exposure -->
    <template v-if="isCompleted">
      <div class="mb-1 border-top pt-3">
        <span class="text-success text-16">
          <strong>
            <i class="bi bi-check-circle-fill me-1"></i>
            Marked Complete
          </strong>
        </span>
      </div>
      <p class="text-14 mb-3">
        Completed in {{ formatSource(item?.source) }} {{ formatShortDate(item?.timestamp) }} by {{ item?.user_fullname || 'Unknown' }}
      </p>

      <div class="mb-3">
        <p class="text-16 mb-1"><strong>Reduced Exposure</strong></p>
        <div v-if="getImprovedRiskScore()" class="text-16 mb-1">
          Improved Risk Score: <strong>{{ getImprovedRiskScore().afterLabel }}</strong>
          <span class="text-muted"> (formerly {{ getImprovedRiskScore().beforeLabel }})</span>
        </div>
        <div v-if="getDecreasedRiskAmount()" class="text-16">
          Decreased Risk Amount: <strong>{{ getDecreasedRiskAmount().afterLabel }}</strong>
          <span class="text-muted"> (formerly {{ getDecreasedRiskAmount().beforeLabel }})</span>
        </div>
        <div v-if="!getImprovedRiskScore() && !getDecreasedRiskAmount()" class="text-16">
          No risk data available for this item.
        </div>
      </div>
    </template>

    <!-- Pending mode: current risk + projected risk -->
    <template v-else>
      <div class="border-top py-3">
        <p class="text-16 mb-1"><strong>Current Risk Score &amp; Amount</strong></p>
        <div class="text-16 mb-1">
          Projected Risk Score: <strong>{{ currentRiskScore }}</strong>
        </div>
        <div class="text-16">
          Projected Risk Amount: <strong>{{ currentRiskAmount }}</strong>
        </div>
      </div>

      <div class="border-top py-3">
        <p class="text-16 mb-1"><strong>Reduced Exposure Once Marked Completed</strong></p>
        <template v-if="isCalculatingProjection">
          <div class="text-16 text-muted">
            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
            Calculating projected risk...
          </div>
        </template>
        <template v-else>
          <div class="text-16 mb-1">
            Projected Risk Score: <strong>{{ projectedRiskScore }}</strong>
          </div>
          <div class="text-16">
            Projected Risk Amount: <strong>{{ projectedRiskAmount }}</strong>
          </div>
        </template>
      </div>
    </template>

    <!-- Collapsible: Referenced Assessment Question -->
    <div class="border-top py-3">
      <div
        class="d-flex justify-content-between align-items-center collapsible-header"
        role="button"
        @click="assessmentQuestionExpanded = !assessmentQuestionExpanded"
      >
        <span class="text-16 text-primary"><strong>Referenced Assessment Question</strong></span>
        <i
          class="bi text-primary"
          :class="assessmentQuestionExpanded ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"
        ></i>
      </div>
      <div v-if="assessmentQuestionExpanded" class="mt-3">
        <span v-if="getQuestionText()" class="text-16">
          <strong>
            <TextWithDefinitions :text="getQuestionText()" />
          </strong>
        </span>
        <p v-else class="text-16 mb-0">Question text not available.</p>
      </div>
    </div>

    <!-- Collapsible: Action Item Definition -->
    <div class="border-top py-3">
      <div
        class="d-flex justify-content-between align-items-center collapsible-header"
        role="button"
        @click="definitionExpanded = !definitionExpanded"
      >
        <span class="text-16 text-primary"><strong>Action Item Definition</strong></span>
        <i
          class="bi text-primary"
          :class="definitionExpanded ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"
        ></i>
      </div>
      <div v-if="definitionExpanded" class="mt-3">
        <span v-if="getDefinitionText()" class="text-16">
          <TextWithDefinitions :text="getDefinitionText()" />
        </span>
        <p v-else class="text-muted mb-0">No definition available.</p>
      </div>
    </div>

    <!-- Collapsible: Notes -->
    <div class="border-top py-3">
      <div
        class="d-flex justify-content-between align-items-center collapsible-header"
        role="button"
        @click="notesExpanded = !notesExpanded"
      >
        <span class="text-16 text-primary"><strong>Notes</strong></span>
        <i
          class="bi text-primary"
          :class="notesExpanded ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"
        ></i>
      </div>
      <div v-if="notesExpanded" class="mt-3">
        <p class="text-16 mb-2"><strong>Assessment Notes</strong></p>
        <template v-if="item?.notes">
          <div class="note-item">
            <div v-if="getUserDisplay()" class="text-14"><strong>{{ getUserDisplay() }}</strong></div>
            <div v-if="getNoteDate()" class="text-gray-600 text-14">Last Edit: {{ getNoteDate() }}</div>
            <p class="mt-2 mb-0 text-14 text-gray-700">{{ item.notes }}</p>
          </div>
        </template>
        <p v-else class="text-14 text-gray-700 mb-0">No notes have been written</p>
      </div>
    </div>

    <!-- Footer buttons -->
    <div class="d-flex justify-content-end button-container">
      <button
        v-if="!isCompleted"
        type="button"
        class="btn btn-primary text-16"
        @click="emit('go-to-mark-complete')"
      >
        Go to Mark Complete
      </button>
      <button
        v-else
        type="button"
        class="btn btn-primary text-16"
        @click="emit('close-panel')"
      >
        Close Panel
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.detail-view {
  padding: 0.5rem 0;
}

.collapsible-header {
  cursor: pointer;
}

.note-item {
  padding-bottom: 0.75rem;
  border-bottom: 1px dotted #dee2e6;
}

.button-container {
  background-color: var(--bs-light);
  padding: 16px;
  position: fixed;
  bottom: 0;
  right: 0;
  width: 510px;
}
</style>
